import {
  Header,
  SpaceBetween,
} from '@cloudscape-design/components';
import PropTypes from 'prop-types';
import React from 'react';

import { formatDuration } from '../../../features/plc/components/common';
import { usePLCValue } from '../../../features/plc/context';
import { usePressHandle, usePressTitle, useProgramHandle } from '../hooks/context';
import PLCButton from './plcButton';

const VARIANTS = {
  forming: {
    disableLabel: 'Disable forming mode',
    stageLabels: {
      0: 'Waiting for Instron to start test',
      10: 'Wait for initial signal from Instron',
      20: 'Acknowledge message by toggling strain',
      30: 'Wait for sound to indicate that heating can start',
      40: 'Heating to setpoint',
      41: 'Presoaking...',
      60: 'Forming...',
      70: 'Cooling to setpoint',
      71: 'Coolsoaking...',
      80: 'Done!',
    },
  },
  bonding: {
    disableLabel: 'Disable bonding mode',
    stageLabels: {
      0: 'Waiting for Instron to start test',
      10: 'Wait for the Instron to be at ready height',
      11: 'Acknowledge message by toggling strain',
      12: 'Wait for the Instron to be at bond height',
      20: 'Start ramping heaters',
      21: 'Wait for heaters to be at temp',
      22: 'Wait until Instron is at presoak height',
      23: 'Wait for presoak time to finish',
      24: 'Wait for Instron to be ready at bond force',
      30: 'Wait for bond time to finish',
      31: 'Wait for press to reach annealing force',
      40: 'Change to superheat temp',
      41: 'Ramping to superheat',
      42: 'Superheating...',
      44: 'Ramping to anneal temp',
      45: 'Annealing...',
      50: 'Cooling...',
      51: 'Wait for heaters to be at temp',
      90: 'Done!',
    },
  },
};

/**
 * Header for the press 1-4 (TM2) forming/bonding HMI: current automation stage,
 * time left in timed stages, and automation/stage durations — all driven by the
 * FB's iProcessState, tCycleElapsed, tStateElapsed and tStateRemaining.
 * Mirrors the TM3 PressHMIHeader, which reads through the newer PLC provider.
 */
export default function AutomationHeader({ variant, extraActions = null }) {
  const { disableLabel, stageLabels } = VARIANTS[variant];
  const pressTitle = usePressTitle();
  const programHandle = useProgramHandle();
  const pressHandle = usePressHandle();

  const automationEnabled = usePLCValue(`${programHandle}.bProcessAutomationEnabled`);
  const automationState = usePLCValue(`${programHandle}.iProcessState`);
  const cycleElapsed = usePLCValue(`${programHandle}.tCycleElapsed`);
  const stateElapsed = usePLCValue(`${programHandle}.tStateElapsed`);
  const stateRemaining = usePLCValue(`${programHandle}.tStateRemaining`);

  const stageLabel = automationEnabled
    ? (stageLabels[automationState ?? 0] || `Unknown Stage (${automationState ?? '--'})`)
    : 'Automation Disabled';

  return (
    <Header
      variant="h1"
      description={`Automation duration: ${formatDuration(cycleElapsed)} | Stage duration: ${formatDuration(stateElapsed)}`}
      actions={(
        <SpaceBetween direction="horizontal" size="xs">
          {extraActions}
          <PLCButton variant="primary" onClick={(write) => { write({ handle: `MAIN.${pressHandle}_Mode`, value: 0 }); }}>
            {disableLabel}
          </PLCButton>
        </SpaceBetween>
      )}
    >
      {[pressTitle, stageLabel, stateRemaining > 0 ? formatDuration(stateRemaining) : null].filter(Boolean).join(' - ')}
    </Header>
  );
}

AutomationHeader.propTypes = {
  variant: PropTypes.oneOf(Object.keys(VARIANTS)).isRequired,
  extraActions: PropTypes.node,
};
